import React, { createContext, useContext, useMemo, useState, ReactNode } from 'react';
import { ThemeProvider as MUIThemeProvider, createTheme } from '@mui/material/styles';

type ThemeMode = 'light' | 'dark';

type ThemeContextType = {
  mode: ThemeMode;
  toggleColorMode: () => void;
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('light');

  const colorMode = useMemo(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    []
  );

  const theme = useMemo(
    () =>
      createTheme({
        palette: {
          mode,
          primary: {
            main: mode === 'light' ? '#4a6fa5' : '#90caf9',
            light: mode === 'light' ? '#7a9dd6' : '#b3d8ff',
            dark: mode === 'light' ? '#1a4476' : '#5d99c6',
            contrastText: '#ffffff',
          },
          secondary: {
            main: mode === 'light' ? '#6c757d' : '#a5d6a7',
            light: mode === 'light' ? '#9fa6ad' : '#d7ffd9',
            dark: mode === 'light' ? '#40484f' : '#75a478',
            contrastText: '#ffffff',
          },
          background: {
            default: mode === 'light' ? '#f8f9fa' : '#121212',
            paper: mode === 'light' ? '#ffffff' : '#1e1e1e',
          },
          text: {
            primary: mode === 'light' ? '#212529' : '#f5f5f5',
            secondary: mode === 'light' ? '#495057' : '#e0e0e0',
          },
        },
        components: {
          MuiButton: {
            styleOverrides: {
              root: {
                textTransform: 'none',
                borderRadius: '8px',
                padding: '8px 20px',
                fontWeight: 500,
                boxShadow: 'none',
                '&:hover': {
                  boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                },
              },
            },
          },
        },
        typography: {
          fontFamily: [
            'Poppins',
            '-apple-system',
            'BlinkMacSystemFont',
            '"Segoe UI"',
            'Roboto',
            '"Helvetica Neue"',
            'Arial',
            'sans-serif',
          ].join(','),
          h1: {
            fontSize: '2.5rem',
            fontWeight: 700,
            marginBottom: '1rem',
          },
          h2: {
            fontSize: '2rem',
            fontWeight: 600,
            marginBottom: '0.75rem',
          },
          h3: {
            fontSize: '1.75rem',
            fontWeight: 600,
            marginBottom: '0.5rem',
          },
        },
      }),
    [mode]
  );

  return (
    <ThemeContext.Provider value={{ mode, ...colorMode }}>
      <MUIThemeProvider theme={theme}>
        {children}
      </MUIThemeProvider>
    </ThemeContext.Provider>
  );
};

export const useThemeContext = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useThemeContext must be used within a ThemeProvider');
  }
  return context;
};
